import type { JsonSchema7, UISchemaElement } from '@jsonforms/core';
import { fieldTypesConfig } from 'src/components/form/custom-attributes/field-types';
import customAttributeRenderers, {
  jsonFormsDispatchRenderer,
} from 'src/components/form/custom-attributes/renderers/collection-layouts/customAttributeRenderers';
import type { FieldRendererProps } from 'src/components/form/custom-attributes/renderers/collection-layouts/types';

import type { JSONObject } from '@/types/types';
import { hasConfiguredAlternateValues } from '@/utils/table/utils/customAttributeHelpers';

import type {
  CustomAttributeSchemaForRendering,
  CustomAttributeSchemaInput,
  FieldConfig,
  TableRecord,
} from '../types';

export const toCustomAttributeSchemas = (
  schemaInput: CustomAttributeSchemaInput
): CustomAttributeSchemaForRendering[] => {
  if (!schemaInput) {
    return [];
  }

  return Array.isArray(schemaInput) ? schemaInput : [schemaInput];
};

/**
 * Retrieve a list of field configs for a give custom attribute schema
 * @param schema
 * @param uiSchema
 * @returns
 */
export const getCustomAttributeRenderProps = (
  schema: JsonSchema7,
  uiSchema: UISchemaElement
): FieldRendererProps[] => {
  const renderers = customAttributeRenderers;

  return jsonFormsDispatchRenderer({
    renderers,
    rootSchema: schema,
    schema,
    uischema: uiSchema,
  });
};

/**
 * Returns an object containing all the custom attribute values
 * @param fields
 * @param record
 * @returns
 */
export const getCustomAttributeDataForRecord = <T extends TableRecord>(
  fields: Record<keyof T, FieldConfig<T>>,
  record: T,
  {
    userLookup,
    groupLookup,
    departmentTypeLookup,
    tagTypeLookup,
  }: {
    userLookup: Record<string, string> | undefined;
    groupLookup: Record<string, string> | undefined;
    departmentTypeLookup: Record<string, string> | undefined;
    tagTypeLookup: Record<string, string> | undefined;
  }
) => {
  return Object.entries(fields).reduce((acc, [key, field]) => {
    if (field.custom) {
      return {
        ...acc,
        [key]: field.customFieldValue(record, {
          userLookup,
          groupLookup,
          departmentTypeLookup,
          tagTypeLookup,
        }),
      };
    }

    return acc;
  }, {});
};

function convertSchemaToFieldConfig<
  T extends {
    CustomAttributeData: JSONObject;
  },
>({
  schemas,
  enableRelativeDates,
  hiddenPaths,
}: {
  schemas: CustomAttributeSchemaForRendering | undefined;
  enableRelativeDates: boolean;
  // Bare schema property paths whose form_field_configuration is Hidden. These
  // are honoured only at record-form render (useControlledCustomAttributes);
  // tabular surfaces (register columns, property filters, smart widget) must
  // exclude them too (CUS-216).
  hiddenPaths?: Set<string>;
}): { [key: string]: FieldConfig<T> } {
  const config: { [key: string]: FieldConfig<T> } = {};
  if (!schemas?.Schema || !schemas?.UiSchema) {
    return config;
  }
  // render column collections from schemas.
  const collections = getCustomAttributeRenderProps(
    schemas?.Schema || {},
    schemas?.UiSchema || {}
  );

  // map collections to config using type.
  collections.forEach((renderProps) => {
    const { path, type, options, altLabel } = renderProps;

    const useAlternateValues = hasConfiguredAlternateValues({
      altLabel,
      options,
    });

    const fieldTypeConfig = fieldTypesConfig[type];
    if (!fieldTypeConfig) {
      console.warn(`No field type config found for type: ${type}`);

      return;
    }
    // Attach-file (and any relation-backed field) is excluded from all tabular
    // surfaces — no register column, and transitively no property filter or
    // widget measure (both derive from these field configs).
    if (fieldTypeConfig.excludeFromReporting) {
      return;
    }
    // Hidden fields are dropped from all tabular surfaces (CUS-216).
    if (hiddenPaths?.has(path)) {
      return;
    }
    config[path] = fieldTypeConfig.getTableFieldConfig(renderProps, {
      enableRelativeDates,
    });

    if (useAlternateValues) {
      config[`${path}_alt`] = {
        ...fieldTypeConfig.getTableFieldConfig(renderProps, {
          enableRelativeDates,
          useAlternateValues,
        }),
        isVirtual: true,
      };
    }
  });

  return config;
}

const CUSTOM_ATTRIBUTE_DATA_PREFIX = 'CustomAttributeData.';

/**
 * Build the set of bare schema property paths whose form field configuration is
 * marked Hidden. `form_field_configuration.FieldId` is
 * `CustomAttributeData.<path>`; the schema/table paths are the bare `<path>`, so
 * strip the prefix. Rows without the prefix (defensive) are kept verbatim.
 */
export const getHiddenCustomAttributePaths = (
  fieldsConfig:
    | { FieldId: string; Hidden?: boolean | null }[]
    | null
    | undefined
): Set<string> => {
  const hidden = new Set<string>();
  fieldsConfig?.forEach((field) => {
    if (!field.Hidden) {
      return;
    }
    hidden.add(
      field.FieldId.startsWith(CUSTOM_ATTRIBUTE_DATA_PREFIX)
        ? field.FieldId.slice(CUSTOM_ATTRIBUTE_DATA_PREFIX.length)
        : field.FieldId
    );
  });

  return hidden;
};

export const convertSchemasToFieldConfigs = ({
  customAttributeSchemas,
  enableRelativeDates,
  hiddenPaths,
}: {
  customAttributeSchemas: CustomAttributeSchemaForRendering[];
  enableRelativeDates: boolean;
  hiddenPaths?: Set<string>;
}): ReturnType<typeof convertSchemaToFieldConfig> => {
  return customAttributeSchemas.reduce((configs, schemas) => {
    return {
      ...configs,
      ...convertSchemaToFieldConfig({
        schemas,
        enableRelativeDates,
        hiddenPaths,
      }),
    };
  }, {});
};
